'use client';

import React, { useState, useRef, useEffect, useMemo } from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';

export interface CustomDateInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  label?: React.ReactNode;
  error?: string;
  variant?: 'dark' | 'light';
  className?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

const labelClassByVariant = {
  dark: 'text-white/90',
  light: 'text-navy-dark',
};

const triggerClassByVariant = {
  dark: 'text-white placeholder-white/40 bg-white/10 border border-white/20',
  light:
    'text-text-body placeholder-text-muted bg-[#fafbfc] border border-border-light',
};

const errorClassByVariant = {
  dark: 'text-red-300',
  light: 'text-red-600',
};

const MESES: string[] = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const DIAS_SEMANA = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

/** Formata YYYY-MM-DD para exibição em pt-BR (dd de mmm. de yyyy ou dd/mm/yyyy) */
function formatDisplay(iso: string, long = true): string {
  if (!iso || iso.length < 10) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return '';
  if (long) {
    const mes = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
    return `${d} de ${mes} de ${y}`;
  }
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Retorna YYYY-MM-DD para o dia; null se mês não contém esse dia */
function toISO(year: number, month: number, day: number): string {
  const d = new Date(year, month, day);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** Gera os dias do mês para o grid (inclui células vazias no início) */
function getDaysForMonth(year: number, month: number): { day: number | null; iso: string | null }[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startWeekDay = first.getDay();
  const totalDays = last.getDate();
  const cells: { day: number | null; iso: string | null }[] = [];

  for (let i = 0; i < startWeekDay; i++) {
    cells.push({ day: null, iso: null });
  }
  for (let d = 1; d <= totalDays; d++) {
    cells.push({ day: d, iso: toISO(year, month, d) });
  }
  return cells;
}

export const CustomDateInput: React.FC<CustomDateInputProps> = ({
  value,
  onChange,
  label,
  error,
  variant = 'light',
  className = '',
  id: idProp,
  required,
  disabled,
  placeholder = 'Selecione a data',
}) => {
    const reactId = React.useId();
    const id = idProp ?? `custom-date-${reactId.replace(/:/g, '')}`;
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const valueDate = useMemo(() => {
      if (!value || value.length < 10) return null;
      const [y, m, d] = value.split('-').map(Number);
      const date = new Date(y, m - 1, d);
      return isNaN(date.getTime()) ? null : date;
    }, [value]);

    const displayText = value ? formatDisplay(value) : '';

    const [viewState, setViewState] = useState<[number, number]>(() => {
      const base = valueDate ?? new Date();
      return [base.getFullYear(), base.getMonth()];
    });

    const [currentViewYear, currentViewMonth] = viewState;

    useEffect(() => {
      if (open && valueDate) {
        setViewState([valueDate.getFullYear(), valueDate.getMonth()]);
      }
    }, [open, valueDate?.getFullYear(), valueDate?.getMonth()]);

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const goPrevMonth = () => {
      if (currentViewMonth === 0) setViewState([currentViewYear - 1, 11]);
      else setViewState([currentViewYear, currentViewMonth - 1]);
    };
    const goNextMonth = () => {
      if (currentViewMonth === 11) setViewState([currentViewYear + 1, 0]);
      else setViewState([currentViewYear, currentViewMonth + 1]);
    };

    const daysForGrid = useMemo(
      () => getDaysForMonth(currentViewYear, currentViewMonth),
      [currentViewYear, currentViewMonth]
    );

    const handleSelect = (iso: string) => {
      onChange(iso);
      setOpen(false);
    };

    const triggerClasses = [
      'w-full rounded-lg box-border',
      'px-3 py-2.5 text-sm min-h-[40px]',
      'flex items-center justify-between gap-2',
      'focus:outline-none focus:border-cyan focus:ring-1 focus:ring-cyan',
      'transition-colors duration-200',
      'disabled:opacity-50 disabled:cursor-not-allowed',
      triggerClassByVariant[variant],
      error ? 'border-red-400 focus:border-red-400 focus:ring-red-400' : '',
    ].filter(Boolean).join(' ');

    return (
      <div
        ref={containerRef}
        className={`w-full min-w-0 max-w-full ${className}`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4">
          {label && (
            <label
              htmlFor={id}
              className={`text-sm font-medium mb-1 sm:mb-0 sm:shrink-0 ${labelClassByVariant[variant]}`}
            >
              {label}
            </label>
          )}
          <div className="w-full min-w-0 sm:flex-1 sm:min-w-0 relative">
            <button
              type="button"
              id={id}
              disabled={disabled}
              aria-haspopup="dialog"
              aria-expanded={open}
              aria-label={displayText || placeholder}
              className={`${triggerClasses} text-left`}
              onClick={() => !disabled && setOpen((prev) => !prev)}
            >
              <span className={`truncate min-w-0 ${displayText ? 'text-text-body' : 'text-text-muted'}`}>
                {displayText || placeholder}
              </span>
              <AppIcon
                name="CalendarDaysIcon"
                size={18}
                className="shrink-0 text-text-muted opacity-70"
                aria-hidden
              />
            </button>

            {open && (
              <div
                role="dialog"
                aria-label="Selecionar data"
                className="absolute z-30 mt-1 left-0 right-0 sm:left-auto sm:right-0 sm:min-w-[280px] w-full max-w-[min(100vw-2rem,320px)] rounded-xl border border-border-light bg-white p-4 shadow-lg"
              >
                <div className="flex items-center justify-between gap-2 mb-3">
                  <button
                    type="button"
                    onClick={goPrevMonth}
                    className="p-1.5 rounded-lg hover:bg-[#f0f4f8] text-text-body focus:outline-none focus:ring-2 focus:ring-cyan"
                    aria-label="Mês anterior"
                  >
                    <AppIcon name="ChevronLeftIcon" size={18} />
                  </button>
                  <span className="text-xs font-semibold text-navy-dark capitalize">
                    {MESES[currentViewMonth]} {currentViewYear}
                  </span>
                  <button
                    type="button"
                    onClick={goNextMonth}
                    className="p-1.5 rounded-lg hover:bg-[#f0f4f8] text-text-body focus:outline-none focus:ring-2 focus:ring-cyan"
                    aria-label="Próximo mês"
                  >
                    <AppIcon name="ChevronRightIcon" size={18} />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-0.5 text-center">
                  {DIAS_SEMANA.map((d) => (
                    <span key={d} className="text-xs font-medium text-text-muted py-1">
                      {d}
                    </span>
                  ))}
                  {daysForGrid.map((cell, i) => {
                    if (cell.day === null) {
                      return <span key={`e-${i}`} />;
                    }
                    const isSelected = value === cell.iso;
                    const isToday = (() => {
                      const t = new Date();
                      return cell.iso === toISO(t.getFullYear(), t.getMonth(), t.getDate());
                    })();
                    return (
                      <button
                        key={cell.iso ?? i}
                        type="button"
                        onClick={() => cell.iso && handleSelect(cell.iso)}
                        className={`
                          w-8 h-8 rounded-lg text-xs font-medium
                          focus:outline-none focus:ring-2 focus:ring-cyan focus:ring-offset-1
                          ${isSelected ? 'bg-cyan text-navy-dark' : 'hover:bg-cyan/20 text-text-body'}
                          ${isToday && !isSelected ? 'ring-1 ring-cyan/50' : ''}
                        `}
                      >
                        {cell.day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
        {error && (
          <p className={`mt-1 text-xs ${errorClassByVariant[variant]}`} role="alert">
            {error}
          </p>
        )}
      </div>
    );
  };

CustomDateInput.displayName = 'CustomDateInput';
